/**
 * Tenant OAuth provider registry — Phase 5 scaffold (Apple · Kakao)
 * Phase 6 S4: BYOIDP clientId resolution (platform_managed vs tenant_owned)
 * @see .cursor/plans/archived/2026-q2/genesis-funnel-completion/stream-g1-identity-federation-phase5.md
 */

import {
  getTenantOAuthClientConfig,
  getTenantOAuthMode,
  isGoogleOAuthExposed,
  type TenantAuthPolicy,
  type TenantOAuthClientProvider,
  type TenantSocialOAuthProvider,
} from './tenant-auth-policy';

/** Providers with tenant login callback routes (Phase 3 Google + Phase 5 extensions). */
export const TENANT_OAUTH_CALLBACK_PATHS = {
  google: '/login/oauth/google',
  apple: '/login/oauth/apple',
  kakao: '/login/oauth/kakao',
} as const;

export type TenantOAuthCallbackProvider = keyof typeof TENANT_OAUTH_CALLBACK_PATHS;

/** Phase 5 optional social extensions (scaffold). */
export const TENANT_OAUTH_PHASE5_PROVIDERS = [
  'apple',
  'kakao',
] as const satisfies readonly TenantSocialOAuthProvider[];

export type TenantOAuthPhase5Provider = (typeof TENANT_OAUTH_PHASE5_PROVIDERS)[number];

export interface TenantOAuthProviderDefinition {
  id: TenantOAuthCallbackProvider;
  displayName: string;
  authorizeUrl: string;
  /** Satisfied when any group has all keys set (non-placeholder). */
  envCredentialGroups: readonly (readonly string[])[];
  defaultScope: string;
}

export const TENANT_OAUTH_PROVIDER_REGISTRY: Record<
  TenantOAuthCallbackProvider,
  TenantOAuthProviderDefinition
> = {
  google: {
    id: 'google',
    displayName: 'Google',
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    envCredentialGroups: [['GOOGLE_OAUTH_CLIENT_ID'], ['GOOGLE_CLIENT_ID']],
    defaultScope: 'openid email profile',
  },
  apple: {
    id: 'apple',
    displayName: 'Apple',
    authorizeUrl: 'https://appleid.apple.com/auth/authorize',
    envCredentialGroups: [['APPLE_OAUTH_CLIENT_ID']],
    defaultScope: 'name email',
  },
  kakao: {
    id: 'kakao',
    displayName: 'Kakao',
    authorizeUrl: 'https://kauth.kakao.com/oauth/authorize',
    envCredentialGroups: [['KAKAO_OAUTH_CLIENT_ID'], ['KAKAO_REST_API_KEY']],
    defaultScope: 'profile_nickname account_email',
  },
};

export interface ResolveTenantOAuthClientIdOptions {
  tenantId?: string;
  /** Loaded tenant auth policy — required for tenant_owned clientId resolution. */
  policy?: TenantAuthPolicy;
}

function readEnv(key: string): string {
  return process.env[key]?.trim() ?? '';
}

function isPlaceholder(value: string): boolean {
  return !value || value.startsWith('YOUR_');
}

function isEnvGroupSatisfied(group: readonly string[]): boolean {
  return group.every((key) => !isPlaceholder(readEnv(key)));
}

function resolvePlatformManagedOAuthClientId(provider: TenantOAuthCallbackProvider): string {
  const definition = TENANT_OAUTH_PROVIDER_REGISTRY[provider];
  for (const group of definition.envCredentialGroups) {
    if (!isEnvGroupSatisfied(group)) continue;
    const clientId = readEnv(group[0] ?? '');
    if (!isPlaceholder(clientId)) return clientId;
  }
  return '';
}

/** True when server env has credentials for the provider (feature-flag gate). */
export function isTenantOAuthProviderConfigured(provider: TenantOAuthCallbackProvider): boolean {
  const definition = TENANT_OAUTH_PROVIDER_REGISTRY[provider];
  return definition.envCredentialGroups.some(isEnvGroupSatisfied);
}

export function listConfiguredTenantOAuthProviders(): TenantOAuthCallbackProvider[] {
  return (Object.keys(TENANT_OAUTH_PROVIDER_REGISTRY) as TenantOAuthCallbackProvider[]).filter(
    isTenantOAuthProviderConfigured
  );
}

/**
 * Resolve OAuth clientId for token exchange / authorize URL.
 * platform_managed → global env; tenant_owned → settings.auth.oauthClients[provider].clientId.
 */
export function resolveTenantOAuthClientId(
  provider: TenantOAuthCallbackProvider,
  opts?: ResolveTenantOAuthClientIdOptions
): string {
  const policy = opts?.policy;
  const oauthMode = policy ? getTenantOAuthMode(policy) : 'platform_managed';

  if (oauthMode === 'tenant_owned' && policy) {
    const tenantClient = getTenantOAuthClientConfig(policy, provider as TenantOAuthClientProvider);
    const clientId = tenantClient?.clientId?.trim() ?? '';
    if (!isPlaceholder(clientId)) return clientId;
    return '';
  }

  return resolvePlatformManagedOAuthClientId(provider);
}

export function isSocialOAuthExposed(
  policy: TenantAuthPolicy,
  provider: TenantSocialOAuthProvider
): boolean {
  if (provider === 'google') {
    return isGoogleOAuthExposed(policy);
  }
  return policy.allowPlatformSSO || policy.allowedSocialProviders.includes(provider);
}

/** Policy + env intersection for login UI button visibility. */
export function getLoginVisibleOAuthProviders(
  policy: TenantAuthPolicy,
  configured: readonly TenantOAuthCallbackProvider[]
): TenantOAuthCallbackProvider[] {
  return configured.filter((provider) => isSocialOAuthExposed(policy, provider));
}

export function getTenantOAuthCallbackPath(provider: TenantOAuthCallbackProvider): string {
  return TENANT_OAUTH_CALLBACK_PATHS[provider];
}
